import { Anchor } from "@svea/design-system-components-react";
import {
	AriaPopover,
	CardContent,
	Heading,
	Markdown,
	moneyFormatNoDecimal,
	Pill,
	SimpleGrid,
	Stack,
	Text,
	useText,
} from "@sveabank/cc-common";
import { Card, CardHeader, P } from "@sveabank/cc-common";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { UspListItem } from "../../../../components/bullet-list/usp-list-item";
import Hide from "../../../../components/hide";
import type { ClcPackage } from "../../../../types/package";
import ClcForetagarnaOfferBlock from "./clc-foretagarna-offer-block";
import { foretagarnaPackageMonthlyPrice } from "./foretagarna-helper";

const ClcSimpleGrid = ({ children }: { children: ReactNode }) => (
	<div className="laptop:grid-cols-2 grid w-full grid-cols-1 gap-4">{children}</div>
);

type Props = {
	basePackage?: ClcPackage;
	foretagarnaPackage?: ClcPackage;
	selectedPackageId?: string;
	onOptimisticSelect: (packageId?: string) => void;
};

const ClcBasePackageBlock = ({
	basePackage,
	foretagarnaPackage,
	selectedPackageId,
	onOptimisticSelect,
}: Props) => {
	const [t, texts] = useText();

	if (!basePackage) return null;

	const isBasePackageSelected = selectedPackageId === basePackage.packageId;

	const packages = texts.newCompany.packages;
	const foretagarnaText = packages["FÖRETAGARNA-SVEA-STARTA-BOLAG"];
	const basText = packages["SVEA-STARTA-BOLAG"];
	const text = isBasePackageSelected ? basText : foretagarnaText;

	const moreAboutText = t(texts.common.moreAbout, t(text.title));
	const basePackagePrice = moneyFormatNoDecimal(Number(basePackage.baseMonthlyFee.amount));
	const totalPrice = moneyFormatNoDecimal(
		Number(basePackage.baseMonthlyFee.amount) +
			(isBasePackageSelected ? 0 : foretagarnaPackageMonthlyPrice),
	);

	return (
		<div>
			<Card plain>
				<Stack gap={4}>
					<CardHeader>
						<ClcSimpleGrid>
							<Stack gap={1} direction="row">
								<Heading.H3>{t(text.title)}</Heading.H3>
								{!isBasePackageSelected && <Text>{t(foretagarnaText.postTitle)}</Text>}
							</Stack>

							<div className="laptop:justify-self-end justify-self-start">
								<Pill text={t(text.label)} status="info" size="sm" />
							</div>
						</ClcSimpleGrid>
					</CardHeader>

					<CardContent>
						<Stack gap={6}>
							<Hide to="tablet">
								<Stack component="ul" gap={4}>
									<SimpleGrid cols={2} gap={1}>
										<UspListItem text={t(text.desktop.bullet1)} />
										<UspListItem text={t(text.desktop.bullet2)} />
										<UspListItem text={t(text.desktop.bullet3)} />
										<UspListItem text={t(text.desktop.bullet4)} />
									</SimpleGrid>
								</Stack>
							</Hide>
							<Hide from="tablet">
								<Stack component="ul" gap={4}>
									<SimpleGrid cols={2} gap={1}>
										<UspListItem text={t(text.mobile.bullet1)} />
										<UspListItem text={t(text.mobile.bullet2)} />
										<UspListItem text={t(text.mobile.bullet3)} />
									</SimpleGrid>
								</Stack>
							</Hide>
							<Stack gap={2}>
								<P className="tablet:block hidden">{t(text.desktop.description)}</P>
								<P className="tablet:hidden block">{t(text.mobile.description)}</P>
								<Anchor as={Link} to={t(text.externalLink)} target="_blank" iconEnd="externalLink">
									{moreAboutText}
								</Anchor>
							</Stack>
							<P className="text-text-secondary text-xs">{t(text.terms)}</P>
						</Stack>
					</CardContent>

					<ClcForetagarnaOfferBlock
						foretagarnaPackage={foretagarnaPackage}
						basePackageId={basePackage.packageId}
						selectedPackageId={selectedPackageId}
						onOptimisticSelect={onOptimisticSelect}
					/>
				</Stack>

				<hr className="mb-2 h-4 border-t border-dashed border-[#DADADA]" />

				<CardContent>
					<Stack direction="row" justify="between">
						<Text>{t(text.footer.title)}</Text>
						<Text className={isBasePackageSelected ? "text-xl" : ""}>
							<span className={isBasePackageSelected ? "font-bold" : ""}>{basePackagePrice}</span>
							{t(texts.newCompany.priceMonth)}
						</Text>
					</Stack>

					{!isBasePackageSelected && (
						<Stack>
							<Stack direction="row" justify="between" align="start">
								<Stack direction="row" gap={1} grow={false} align="center">
									<P className="w-min sm:w-auto">{t(foretagarnaText.footer.membership)}</P>
									<div className="-mt-5 sm:mt-0">
										<AriaPopover
											variant="info"
											content={() => (
												<Markdown allowParagraphs>
													{t(foretagarnaText.footer.membershipPopover)}
												</Markdown>
											)}
										/>
									</div>
								</Stack>

								<Text className="self-center whitespace-nowrap">
									{t(
										texts.newCompany.pricePerMonth,
										moneyFormatNoDecimal(foretagarnaPackageMonthlyPrice),
									)}
								</Text>
							</Stack>

							<Stack direction="row" justify="between">
								<Heading.H4>{t(foretagarnaText.footer.total)}</Heading.H4>
								<Text>
									<span className="text-xl font-bold">
										{t(texts.newCompany.priceKr, totalPrice)}
									</span>
									{t(texts.newCompany.monthlyPeriod)}
								</Text>
							</Stack>
						</Stack>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default ClcBasePackageBlock;
